import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtNum, statusClass } from "../lib/format";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

export default function Vulnerabilities() {
  const [d, setD] = useState(null);
  const [topAssets, setTopAssets] = useState([]);
  const [topCves, setTopCves] = useState([]);
  const [domain, setDomain] = useState("All");
  const [severity, setSeverity] = useState("All");
  const nav = useNavigate();

  useEffect(() => {
    apiGet("/vuln/summary", { domain, severity }).then(setD);
  }, [domain, severity]);

  useEffect(() => {
    apiGet("/vuln/top-assets").then(setTopAssets);
    apiGet("/vuln/top-cves").then(setTopCves);
  }, []);

  if (!d) return <div className="p-8 opacity-60">Loading…</div>;

  const drillBase = `/drill/vulnerabilities${domain !== "All" ? `?domain=${domain}` : ""}${severity !== "All" ? `${domain !== "All" ? "&" : "?"}severity=${severity}` : ""}`;

  return (
    <>
      <Masthead
        module="vuln"
        eyebrow="Exposure Management"
        title="Vulnerability Management"
        subtitle="Slice by domain & severity · SLA attainment · daily intake & cumulative backlog"
        meta={<div>Affected hosts: <strong>32 hosts</strong><br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        <div className="flex flex-wrap items-end gap-4 mb-5">
          <div>
            <label className="small-caps text-[12px] block mb-1" style={{ color: "var(--cv-charcoal-soft)" }}>Domain</label>
            <Select value={domain} onValueChange={setDomain}>
              <SelectTrigger data-testid="filter-domain" className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["All", "Workstations", "Servers", "Network", "Application"].map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="small-caps text-[12px] block mb-1" style={{ color: "var(--cv-charcoal-soft)" }}>Severity</label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid="filter-severity" className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["All", "Critical", "High", "Medium", "Low"].map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <Kpi testid="vuln-tracked" label="Tracked Vulnerabilities" value={d.tracked} sub="across all domains" onClick={() => nav(drillBase)} />
          <Kpi testid="vuln-exp" label="Exploitable / Weaponised" value={d.exploitable} sub="known exploit available" alert onClick={() => nav("/drill/vulnerabilities?exploitable=true")} />
          <Kpi testid="vuln-sla" label="SLA Attainment" value={`${d.sla_attainment}%`} sub="remediated within window" onClick={() => nav(drillBase)} />
          <Kpi testid="vuln-breach" label="SLA Breached" value={d.sla_breached} sub="past remediation deadline" alert onClick={() => nav("/drill/vulnerabilities?breached=true")} />
          <Kpi testid="vuln-net" label="30-Day Net Change" value="+1.6K" sub="backlog growing" alert onClick={() => nav(drillBase)} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-domain-breakdown" title="Open Vulnerabilities by Domain" subtitle="Stacked by severity">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.domain_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7DCCD" />
                  <XAxis dataKey="domain" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="Critical" stackId="a" fill="#A5300F" />
                  <Bar dataKey="High" stackId="a" fill="#C75A3A" />
                  <Bar dataKey="Medium" stackId="a" fill="#E5C243" />
                  <Bar dataKey="Low" stackId="a" fill="#4A5A6A" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testid="panel-severity-split" title="Severity Split" subtitle={`${fmtNum(d.filtered_total)} matching open`}>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.severity_split} dataKey="value" nameKey="label" innerRadius={55} outerRadius={95} paddingAngle={2}>
                    {d.severity_split.map((s) => <Cell key={s.label} fill={s.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[13px] mt-2 text-center" style={{ color: "var(--cv-charcoal-soft)" }}>
              {d.severity_split.map((s) => `${s.label} ${fmtNum(s.value)}`).join(" · ")}
            </div>
          </Panel>
        </div>

        <Panel testid="panel-funnel" title="Risk Funnel · Rules-based (legacy)" subtitle="Funnel from all open → actively exploitable, verified-risk subset">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {d.funnel.map((f, i) => (
              <button
                key={f.label}
                data-testid={`funnel-${i}`}
                onClick={() => nav(i >= 5 ? "/drill/vulnerabilities?exploitable=true" : drillBase)}
                className="cv-tile cv-tile-blue text-left"
              >
                <div className="cv-tile-label">{f.label}</div>
                <div className="cv-tile-val">{fmtNum(f.value)}</div>
              </button>
            ))}
          </div>
        </Panel>

        {/* Wiz / Tenable-style Risk Prioritisation */}
        {d.risk_prioritisation && (
          <Panel testid="panel-risk-prio"
                 title="Vulnerability Risk Prioritisation · Wiz / Tenable-style"
                 subtitle={d.risk_prioritisation.formula}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {d.risk_prioritisation.buckets.map((b) => {
                const tone = b.key === "critical" ? "var(--cv-crimson)"
                  : b.key === "high" ? "#D55816"
                  : b.key === "medium" ? "#E5C243" : "var(--cv-slate)";
                return (
                  <button type="button" key={b.key}
                          data-testid={`prio-bucket-${b.key}`}
                          onClick={() => nav("/drill/vulnerabilities?exploitable=true")}
                          className="cv-tile text-left hover:shadow-md transition-shadow"
                          style={{ cursor: "pointer", borderTop: `3px solid ${tone}` }}>
                    <div className="cv-tile-label">{b.label}</div>
                    <div className="cv-tile-value" style={{ color: tone }}>{fmtNum(b.count)}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--cv-charcoal-soft)" }}>
                      {b.share_pct}% of open · {b.band}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="rounded-md px-3 py-2 mb-3 text-[12.5px]"
                 style={{ background: "var(--cv-sand)", border: "1px dashed var(--cv-line)", color: "var(--cv-charcoal)" }}>
              <strong>Insight ·</strong> {d.risk_prioritisation.insight}
            </div>

            <div className="text-[11.5px] small-caps font-bold tracking-[0.1em] mb-1" style={{ color: "var(--cv-dark-grey)" }}>
              Top prioritised CVEs
            </div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left small-caps" style={{ color: "var(--cv-dark-grey)", borderBottom: "1px solid var(--cv-line)" }}>
                    <th className="py-2 px-2 font-bold">CVE</th>
                    <th className="py-2 px-2 font-bold">Vulnerability</th>
                    <th className="py-2 px-2 font-bold text-right">CVSS</th>
                    <th className="py-2 px-2 font-bold text-right">EPSS</th>
                    <th className="py-2 px-2 font-bold">Exploit</th>
                    <th className="py-2 px-2 font-bold">Asset · crit</th>
                    <th className="py-2 px-2 font-bold text-center">Net-facing</th>
                    <th className="py-2 px-2 font-bold text-right">Risk</th>
                    <th className="py-2 px-2 font-bold">Bucket</th>
                  </tr>
                </thead>
                <tbody>
                  {d.risk_prioritisation.top_prioritised.map((v) => {
                    const bTone = v.bucket === "Critical" ? "var(--cv-crimson)"
                      : v.bucket === "High" ? "#D55816"
                      : v.bucket === "Medium" ? "#E5C243" : "var(--cv-slate)";
                    return (
                      <tr key={v.cve} data-testid={`prio-cve-${v.cve}`}
                          onClick={() => nav(`/drill/vulnerabilities?q=${encodeURIComponent(v.cve)}`)}
                          className="hover:bg-orange-50 cursor-pointer transition-colors"
                          style={{ borderBottom: "1px solid var(--cv-line)" }}>
                        <td className="py-2 px-2 font-bold" style={{ color: "var(--cv-crimson)" }}>{v.cve}</td>
                        <td className="py-2 px-2" style={{ color: "var(--cv-charcoal)" }}>
                          <div className="font-bold">{v.name}</div>
                          <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>{v.asset} · {v.domain}</div>
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums font-bold">{v.cvss}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{v.epss}%</td>
                        <td className="py-2 px-2 text-[12px]">
                          <span style={{ color: v.exploitability === "Actively exploited" ? "var(--cv-crimson)"
                            : v.exploitability === "Weaponised" ? "#D55816" : "var(--cv-charcoal-soft)" }}>
                            {v.exploitability}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-[12px]" style={{ color: v.asset_criticality === "Crown Jewel" ? "var(--cv-crimson)" : "var(--cv-charcoal-soft)" }}>
                          {v.asset_criticality}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <span className="status-pill" style={{
                            background: v.internet_facing ? "rgba(165,48,15,0.14)" : "rgba(74,90,106,0.10)",
                            color: v.internet_facing ? "var(--cv-crimson)" : "var(--cv-slate)",
                            fontSize: 10,
                          }}>{v.internet_facing ? "Public" : "Internal"}</span>
                        </td>
                        <td className="py-2 px-2 text-right font-serif-display tabular-nums" style={{ color: bTone, fontSize: 15 }}>
                          {v.risk}
                        </td>
                        <td className="py-2 px-2">
                          <span className="status-pill" style={{ background: `${bTone}22`, color: bTone, fontSize: 10 }}>{v.bucket}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5 mb-5">
          <Panel testid="panel-daily" title="Vulnerabilities Added vs Remediated (Daily)" subtitle="Intake should sit below remediation to drain backlog">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.daily_flow}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7DCCD" />
                  <XAxis dataKey="d" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="added" stroke="#A5300F" dot={false} />
                  <Line type="monotone" dataKey="remediated" stroke="#3E7C4F" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testid="panel-backlog" title="Cumulative Open Backlog" subtitle="Running total, last 30 days">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.cumulative_backlog}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7DCCD" />
                  <XAxis dataKey="d" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="open" stroke="#E5C243" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-ageing" title="Open Vulnerabilities by Age" subtitle="Older bands have breached more SLA windows">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.ageing}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7DCCD" />
                  <XAxis dataKey="band" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#A5300F" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testid="panel-scan" title="Scanner Coverage by Domain" subtitle="Gaps mean blind spots">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-3 py-2 small-caps">Domain</th>
                  <th className="text-right px-3 py-2 small-caps">Coverage</th>
                  <th className="text-right px-3 py-2 small-caps">Assets</th>
                  <th className="text-right px-3 py-2 small-caps">Scanned</th>
                </tr>
              </thead>
              <tbody>
                {d.scan_coverage.map((s) => (
                  <tr key={s.domain} className="cv-row-clickable" onClick={() => nav(`/drill/vulnerabilities?domain=${s.domain}`)}>
                    <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{s.domain}</strong></td>
                    <td className="px-3 py-2 border-b text-right tabular-nums font-bold" style={{ borderColor: "var(--cv-line)", color: s.coverage >= 95 ? "var(--cv-green)" : "#E5C243" }}>{s.coverage}%</td>
                    <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(s.assets)}</td>
                    <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(s.scanned)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>

        <div className="cv-section-label">Top Exposures</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel testid="panel-top-assets" title="Top Vulnerable Assets" subtitle="Most-exposed hosts in current selection">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Asset</th>
                  <th className="text-left px-2 py-2 small-caps">Domain</th>
                  <th className="text-right px-2 py-2 small-caps">Critical</th>
                  <th className="text-right px-2 py-2 small-caps">High</th>
                </tr>
              </thead>
              <tbody>
                {topAssets.map((a) => (
                  <tr key={a.asset} className="cv-row-clickable" onClick={() => nav(`/drill/vulnerabilities?q=${encodeURIComponent(a.asset)}`)}>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{a.asset}</strong></td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{a.domain}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums font-bold" style={{ borderColor: "var(--cv-line)", color: "var(--cv-crimson)" }}>{a.critical}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums font-bold" style={{ borderColor: "var(--cv-line)", color: "#E5C243" }}>{a.high}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel testid="panel-top-cves" title="Top CVEs by Prevalence" subtitle="Highest instance count across the estate">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">CVE</th>
                  <th className="text-right px-2 py-2 small-caps">CVSS</th>
                  <th className="text-left px-2 py-2 small-caps">Name</th>
                  <th className="text-right px-2 py-2 small-caps">Instances</th>
                </tr>
              </thead>
              <tbody>
                {topCves.map((c) => (
                  <tr key={c.cve} className="cv-row-clickable" onClick={() => nav(`/drill/vulnerabilities?q=${encodeURIComponent(c.cve)}`)}>
                    <td className="px-2 py-2 border-b font-mono text-[14px]" style={{ borderColor: "var(--cv-line)" }}><strong>{c.cve}</strong></td>
                    <td className="px-2 py-2 border-b text-right tabular-nums font-bold" style={{ borderColor: "var(--cv-line)", color: c.cvss >= 9 ? "var(--cv-crimson)" : "#E5C243" }}>{c.cvss}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{c.name}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{c.instances}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>
      </div>
    </>
  );
}
